import React from "react";
import { Network } from "lucide-react";
import { wib, mbps } from "./useFireboxLive";

// Network topology of a REMOTE Firebox (Medan / Dumai), built entirely from its own SNMP data (fireboxRemote.js):
// external interfaces on top, the Firebox with its 8 ports, internal segments below, BOVPN peers on the right.
// Zones are inferred from the routing table (SNMP has no zone object): default route / public network = External.

const PORT_C = { 0: "#dc2626", 1: "#15803d", 2: "#f59e0b", 3: "#7e22ce", 4: "#0f766e", 5: "#1d4ed8", 6: "#be185d", 7: "#64748b" };
const TINT = { 0: "#fde2e2", 1: "#dcfce7", 2: "#fef3c7", 3: "#f3e8ff", 4: "#ccfbf1", 5: "#dbeafe", 6: "#fce7f3", 7: "#e2e8f0" };
const OK = "#16a34a", DOWN = "#dc2626", OFF = "#94a3b8";
const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

// Public peer IP -> what it is (Jakarta T80, sites, AWS Singapore PIL VPN endpoints from net_vpn_tunnels).
export const PEERS = {
  "222.165.199.102": "Jakarta T80 (Velo)",
  "202.162.195.163": "Medan M390 (Nusanet)",
  "103.94.126.130": "Dumai M290 (IconPlus)",
  "36.95.179.155": "Kijing T85 (endpoint 2)",
  "58.147.187.26": "Kijing T85 (endpoint 1)",
  "13.213.86.53": "AWS Singapore (PIL) · VPN Medan",
  "13.250.201.102": "AWS Singapore (PIL) · VPN Medan",
  "3.0.194.115": "AWS Singapore (PIL) · VPN Dumai",
  "18.138.127.188": "AWS Singapore (PIL) · VPN Dumai",
};
export const peerName = (ip) => PEERS[ip] || "Peer " + ip;
const isAws = (ip) => /AWS/.test(PEERS[ip] || "");

const ip2n = (ip) => String(ip).split(".").reduce((a, x) => a * 256 + Number(x), 0);
const inCidr = (ip, c) => {
  const [net, b] = String(c).split("/"); const bits = b == null ? 32 : Number(b);
  const m = bits === 0 ? 0 : (0xffffffff << (32 - bits)) >>> 0;
  return ((ip2n(ip) & m) >>> 0) === ((ip2n(net) & m) >>> 0);
};
const ts = (s) => new Date(String(s).replace(" ", "T") + "Z").getTime();

// Shared by the topology and the system-manager tree.
export function derive(L, prev, S) {
  const dt = L && prev ? (ts(L.checked_at) - ts(prev.checked_at)) / 1000 : 0;
  const rate = (f) => {
    const p = prev && dt > 30 ? (prev.ifs || []).find((x) => x.idx === f.idx) : null;
    if (!p) return null;
    const d = (a, b) => (a != null && b != null && a >= b ? ((a - b) * 8) / dt / 1e6 : null);
    return { inMbps: d(f.in, p.in), outMbps: d(f.out, p.out) };
  };
  const peerIps = (S && S.peers ? S.peers.map((p) => p.ip) : []);
  const all = ((L && L.ifs) || []).map((f) => ({
    ...f, rate: rate(f),
    endpoint: f.zone === "External" ? peerIps.find((ip) => (f.nets || []).some((n) => inCidr(ip, n)) || (f.ips || []).some((c) => c.split("/")[0] === ip)) : null,
  }));
  const IF = all.filter((f) => !f.vif).sort((a, b) => a.eth - b.eth);
  const VIF = all.filter((f) => f.vif);
  const sas = (L && L.sas) || [];
  const anyAny = (x) => x.local === "0.0.0.0/0" && x.remote === "0.0.0.0/0";
  const byPeer = {};
  sas.filter((x) => !(VIF.length && anyAny(x))).forEach((x) => { (byPeer[x.peer] = byPeer[x.peer] || []).push(x); });
  const order = (ip) => (/Jakarta/.test(PEERS[ip] || "") ? 0 : isAws(ip) ? 2 : 1);
  const groups = Object.keys(byPeer).sort((a, b) => order(a) - order(b) || a.localeCompare(b))
    .map((peer) => ({ peer, name: peerName(peer), t: byPeer[peer] }));
  const vifPeers = VIF.length ? [...new Set(sas.filter(anyAny).map((x) => x.peer))] : [];
  return { IF, VIF, groups, vifPeers, sas };
}

const st = (f) => (f.zone === "Disabled" || f.zone === "Unused" ? "off" : f.oper === "up" ? "up" : "down");
const stC = (s) => (s === "up" ? OK : s === "down" ? DOWN : OFF);

function Cloud({ cx, color, tint, title, sub, net }) {
  return (
    <g>
      <path transform={"translate(" + (cx - 135) + ",30) scale(2)"} vectorEffect="non-scaling-stroke"
        d="M25,60 a20,20 0 0,1 0,-40 a25,25 0 0,1 45,-10 a22,22 0 0,1 40,10 a20,20 0 0,1 0,40 z" fill={tint} stroke={color} strokeWidth="2" />
      <text x={cx} y={88} textAnchor="middle" fontSize="16" fontWeight="700" fill="#0f172a">{String(title).slice(0, 24)}</text>
      <text x={cx} y={108} textAnchor="middle" fontSize="12.5" fill="#334155">{sub}</text>
      <text x={cx} y={127} textAnchor="middle" fontSize="13" fontWeight="600" fill="#0f172a" fontFamily={MONO}>{net}</text>
    </g>
  );
}

function IfBox({ f, cx, y, w = 230 }) {
  const c = PORT_C[f.eth] || "#64748b";
  const s = st(f);
  const rows = [String(f.name).slice(0, 26), (f.ips && f.ips[0]) || ((f.nets && f.nets[0]) ? "net " + f.nets[0] : "no address")];
  if (f.gw) rows.push("GW: " + f.gw);
  if (f.rate && (f.rate.inMbps != null || f.rate.outMbps != null)) rows.push("↑ " + mbps(f.rate.outMbps) + "  ↓ " + mbps(f.rate.inMbps));
  const h = 32 + rows.length * 19 + 8;
  return (
    <g>
      <title>{"eth" + f.eth + " " + f.name + " - " + f.zone + " (inferred)" + (s === "down" ? " · link down" : "")}</title>
      <rect x={cx - w / 2} y={y} width={w} height={h} rx="8" fill="#fff" stroke={c} strokeWidth="2" />
      <path d={"M" + (cx - w / 2) + "," + (y + 28) + " v-20 a8,8 0 0,1 8,-8 h" + (w - 16) + " a8,8 0 0,1 8,8 v20 z"} fill={c} />
      <text x={cx} y={y + 19} textAnchor="middle" fontSize="13.5" fontWeight="700" fill="#fff">{"eth" + f.eth + " - " + f.zone}</text>
      {rows.map((r, i) => (
        <text key={i} x={cx} y={y + 48 + i * 19} textAnchor="middle" fontSize={i ? 13 : 14} fontWeight={i ? 400 : 700} fill={i === 3 ? "#475569" : "#0f172a"} fontFamily={i ? MONO : undefined}>{r}</text>
      ))}
      <circle cx={cx + w / 2 - 12} cy={y + 14} r="5" fill={stC(s)} stroke="#fff" strokeWidth="1.5" />
    </g>
  );
}

const PORT_X = (n) => 420 + n * 64;
const DEV = { x: 270, y: 390, w: 660, h: 70 };
const Dot = ({ x, y, s }) => <circle cx={x} cy={y} r="7" fill={stC(s)} stroke="#fff" strokeWidth="2" />;
const ANIM_CSS = ".wgr2-led{animation:wgr2Blink 1.4s ease-in-out infinite}@keyframes wgr2Blink{0%,100%{opacity:1}50%{opacity:.2}}" +
  ".wgr2-flow{stroke-dasharray:6 14;animation:wgr2Flow 1s linear infinite}@keyframes wgr2Flow{to{stroke-dashoffset:-20}}" +
  ".wgr2-vpn{animation:wgr2Vpn .9s linear infinite}@keyframes wgr2Vpn{to{stroke-dashoffset:-12}}.wgr2-dead{opacity:.6}" +
  "@media (prefers-reduced-motion: reduce){.wgr2-led,.wgr2-flow,.wgr2-vpn{animation:none}}";
function Link({ d, color, s, w = 2.5 }) {
  if (s === "up") return (<g><path d={d} stroke={color} strokeWidth={w} fill="none" /><path className="wgr2-flow" d={d} stroke="#fff" strokeOpacity="0.9" strokeWidth={w - 0.8} strokeLinecap="round" fill="none" /></g>);
  return <path className="wgr2-dead" d={d} stroke={s === "down" ? DOWN : OFF} strokeWidth={w} strokeDasharray="4 6" fill="none" />;
}
const spread = (n, a, b) => (n <= 1 ? [(a + b) / 2] : Array.from({ length: n }, (_, i) => a + (i * (b - a)) / (n - 1)));

export default function WgRemoteTopology({ S, L, prev }) {
  if (!L) return null;
  const { IF, VIF, groups, vifPeers } = derive(L, prev, S);
  const model = S.model.replace(/^Firebox /, "");
  const ext = IF.filter((f) => f.zone === "External").sort((a, b) => (b.oper === "up") - (a.oper === "up")).slice(0, 4);
  const intr = IF.filter((f) => f.zone === "Trusted" && (f.oper === "up" || (f.nets || []).length))
    .sort((a, b) => (b.oper === "up") - (a.oper === "up") || a.eth - b.eth).slice(0, 4);
  const TOP = ext.map((f, i, arr) => ({ f, cx: spread(arr.length, 170, 830)[i] }));
  const BOTTOM = intr.map((f, i, arr) => ({ f, cx: spread(arr.length, 130, 865)[i], lane: 490 + i * 15 }));
  const inUse = IF.filter((f) => st(f) !== "off");
  const upCount = inUse.filter((f) => f.oper === "up").length;

  const PX = 1040, PW = 450, ROW = 18;
  let cy = 58;
  const cards = groups.map((g) => { const h = 56 + g.t.length * ROW + 10; const c = { g, y: cy, h }; cy += h + 14; return c; });
  const vifCard = VIF.length ? { y: cy, h: 56 + VIF.length * ROW + (vifPeers.length ? ROW : 0) + 10 } : null;
  if (vifCard) cy += vifCard.h + 10;
  const H = Math.max(830, cy + 10);
  const nSa = groups.reduce((a, g) => a + g.t.length, 0);

  return (
    <div style={{ background: "var(--card-bg, #fff)", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 16 }}><Network size={18} /> Network Topology — {(L.sys && L.sys.name) || S.host}</div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
            WatchGuard {model} · Fireware {(L.sys && L.sys.version) || "-"} · {upCount}/{inUse.length} in-use interfaces up · {nSa} BOVPN SAs to {groups.length} peers
            {VIF.length ? ` · ${VIF.filter((v) => v.oper === "up").length}/${VIF.length} VPN interfaces up` : ""} · live SNMP via Jakarta LAN, checked {wib(L.checked_at)}
          </div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>Zones inferred from the routing table (default route / public network = External, private = Trusted) · tunnels are the IPsec SAs this Firebox reports</div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 14, fontSize: 12, color: "#475569" }}>
          <span><b style={{ color: OK }}>●</b> Up</span><span><b style={{ color: DOWN }}>●</b> Down</span><span><b style={{ color: OFF }}>●</b> Disabled / unused</span>
          <span><span style={{ display: "inline-block", width: 22, borderTop: "2px dashed " + OK, verticalAlign: "middle", marginRight: 4 }} />BOVPN</span>
        </div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <svg viewBox={"0 0 1500 " + H} style={{ width: "100%", minWidth: 900, height: "auto", display: "block" }} fontFamily="Inter, system-ui, sans-serif">
          <style>{ANIM_CSS}</style>
          {TOP.map(({ f, cx }) => {
            const s = st(f), px = PORT_X(f.eth), c = PORT_C[f.eth];
            return (
              <g key={f.idx}>
                <Link d={"M" + cx + ",150 V200"} color={c} s={s} />
                <Link d={"M" + cx + ",250 V345 H" + px + " V" + (DEV.y + 23)} color={c} s={s} />
                <Cloud cx={cx} color={c} tint={TINT[f.eth]} title={f.name} sub={f.defaultRoute ? "Default route (internet)" : f.endpoint ? "BOVPN endpoint " + f.endpoint : "WAN"} net={(f.nets && f.nets[0]) || ""} />
                <Dot x={cx} y={175} s={s} />
                <IfBox f={f} cx={cx} y={200} />
              </g>
            );
          })}
          <g>
            <rect x={DEV.x} y={DEV.y} width={DEV.w} height={DEV.h} rx="6" fill="#1f2937" stroke="#0f172a" />
            <rect x={DEV.x} y={DEV.y} width="110" height={DEV.h} rx="6" fill="#dc2626" />
            <text x={DEV.x + 55} y={DEV.y + 30} textAnchor="middle" fontSize="13" fontWeight="700" fill="#fff">WatchGuard</text>
            <text x={DEV.x + 55} y={DEV.y + 52} textAnchor="middle" fontSize="20" fontWeight="800" fill="#fff">{model}</text>
            {[0, 1, 2, 3, 4, 5, 6, 7].map((n) => {
              const f = IF.find((x) => x.eth === n) || { eth: n, name: "eth" + n, zone: "Unused" };
              const s = st(f), x = PORT_X(n);
              return (
                <g key={n}>
                  <title>{"eth" + n + ": " + f.name + " [" + f.zone + (s === "down" ? ":Down" : "") + "]"}</title>
                  <text x={x} y={DEV.y + 16} textAnchor="middle" fontSize="11" fill="#e2e8f0">{"eth" + n}</text>
                  <rect x={x - 19} y={DEV.y + 23} width="38" height="28" rx="3" fill="#0b1220" stroke={s === "off" ? "#64748b" : PORT_C[n]} strokeWidth="3" />
                  {s !== "off" ? <rect className={s === "up" ? "wgr2-led" : undefined} style={{ animationDelay: n * 0.17 + "s" }} x={x - 15} y={DEV.y + 27} width="9" height="5" rx="1" fill={s === "up" ? "#22c55e" : DOWN} /> : null}
                  <circle className={s === "up" ? "wgr2-led" : undefined} cx={x} cy={DEV.y + 61} r="3" fill={stC(s)} />
                </g>
              );
            })}
          </g>
          <text x={DEV.x - 12} y={DEV.y + 22} textAnchor="end" fontSize="15" fontWeight="700" fill="#0f172a">{(L.sys && L.sys.name) || S.host}</text>
          <text x={DEV.x - 12} y={DEV.y + 40} textAnchor="end" fontSize="12" fill="#475569">WatchGuard {model} · {S.label}</text>
          <text x={DEV.x - 12} y={DEV.y + 57} textAnchor="end" fontSize="12" fill="#475569">SNMP {S.mgmt.ip}</text>
          {BOTTOM.map(({ f, cx, lane }) => {
            const s = st(f), px = PORT_X(f.eth), c = PORT_C[f.eth];
            const hops = [...new Set((f.routes || []).map((r) => r.split(" via ")[1]))];
            const lines = [];
            (f.nets || []).slice(1, 3).forEach((n) => lines.push(n));
            if ((f.routes || []).length) lines.push(f.routes.length + " static route" + (f.routes.length > 1 ? "s" : "") + " via " + hops.slice(0, 2).join(", "));
            if (!lines.length) lines.push(s === "up" ? "connected segment" : "link down");
            return (
              <g key={f.idx}>
                <Link d={"M" + px + "," + (DEV.y + DEV.h) + " V" + lane + " H" + cx + " V560"} color={c} s={s} />
                <Dot x={px} y={DEV.y + DEV.h + 14} s={s} />
                <Link d={"M" + cx + ",600 V690"} color={c} s={s} w={2} />
                <IfBox f={f} cx={cx} y={560} w={225} />
                <rect x={cx - 112} y={690} width="224" height="118" rx="10" fill={TINT[f.eth]} fillOpacity="0.45" stroke={c} strokeDasharray="6 4" />
                <text x={cx} y={715} textAnchor="middle" fontSize="14" fontWeight="700" fill={c}>{String(f.name).slice(0, 24)}</text>
                <text x={cx} y={735} textAnchor="middle" fontSize="13" fill="#0f172a" fontFamily={MONO}>{(f.nets && f.nets[0]) || "-"}</text>
                {lines.slice(0, 3).map((l, i) => <text key={i} x={cx} y={760 + i * 18} textAnchor="middle" fontSize="12" fill="#334155" fontFamily={/\d/.test(l[0]) ? MONO : undefined}>{l}</text>)}
              </g>
            );
          })}
          {IF.filter((f) => st(f) === "off").map((f) => <text key={f.idx} x={PORT_X(f.eth)} y={DEV.y - 10} textAnchor="middle" fontSize="10" fill={OFF}>{f.zone === "Disabled" ? "disabled" : "unused"}</text>)}

          <rect x={PX - 10} y={14} width={PW + 20} height={H - 24} rx="12" fill="#f0fdfa" fillOpacity="0.5" stroke="#5eead4" strokeDasharray="6 4" />
          <text x={PX} y={42} fontSize="16" fontWeight="700" fill="#0f766e">Branch Office VPN Tunnels</text>
          {!groups.length && <text x={PX} y={70} fontSize="12.5" fill="#94a3b8">No IPsec SAs reported by this Firebox.</text>}
          {cards.map((c, k) => {
            const ty = c.y + 24, sy = DEV.y + 8 + k * 9, vx = 980 + k * 8;
            return (
              <g key={c.g.peer}>
                <path className="wgr2-vpn" d={"M" + (DEV.x + DEV.w) + "," + sy + " H" + vx + " V" + ty + " H" + PX} stroke={OK} strokeWidth="2" strokeDasharray="7 5" fill="none" />
                <rect x={PX} y={c.y} width={PW} height={c.h} rx="10" fill="#fff" stroke="#cbd5e1" />
                <Dot x={PX} y={ty} s="up" />
                <text x={PX + 18} y={c.y + 24} fontSize="14" fontWeight="700" fill="#0f172a">{c.g.name.length > 40 ? c.g.name.slice(0, 40) + "…" : c.g.name}</text>
                <rect x={PX + PW - 62} y={c.y + 10} width="50" height="20" rx="5" fill="#dcfce7" stroke={OK} />
                <text x={PX + PW - 37} y={c.y + 24} textAnchor="middle" fontSize="11.5" fontWeight="700" fill={OK}>UP</text>
                <text x={PX + 18} y={c.y + 43} fontSize="12.5" fill="#475569" fontFamily={MONO}>{"peer " + c.g.peer + " · " + c.g.t.length + " SA" + (c.g.t.length > 1 ? "s" : "")}</text>
                {c.g.t.map((r, i) => <text key={i} x={PX + 18} y={c.y + 64 + i * ROW} fontSize="12" fontFamily={MONO} fill="#0f172a">{"● " + r.local + "  →  " + r.remote}</text>)}
              </g>
            );
          })}
          {vifCard && (() => {
            const nUp = VIF.filter((v) => v.oper === "up").length;
            const col = nUp === VIF.length ? OK : nUp ? "#f59e0b" : DOWN;
            const ty = vifCard.y + 24;
            return (
              <g>
                <path className={nUp ? "wgr2-vpn" : "wgr2-dead"} d={"M" + (DEV.x + DEV.w) + "," + (DEV.y + 8 + cards.length * 9) + " H" + (980 + cards.length * 8) + " V" + ty + " H" + PX} stroke={col} strokeWidth="2" strokeDasharray="7 5" fill="none" />
                <rect x={PX} y={vifCard.y} width={PW} height={vifCard.h} rx="10" fill="#fff" stroke="#cbd5e1" />
                <circle cx={PX} cy={ty} r="7" fill={col} stroke="#fff" strokeWidth="2" />
                <text x={PX + 18} y={vifCard.y + 24} fontSize="14" fontWeight="700" fill="#0f172a">AWS Singapore (PIL) VPN (route-based)</text>
                <text x={PX + 18} y={vifCard.y + 43} fontSize="12.5" fill="#475569">{nUp + " of " + VIF.length + " VPN interfaces up"}</text>
                {VIF.map((v, i) => (
                  <text key={v.idx} x={PX + 18} y={vifCard.y + 64 + i * ROW} fontSize="12" fontFamily={MONO} fill={v.oper === "up" ? "#0f172a" : "#94a3b8"}>
                    {(v.oper === "up" ? "● " : "○ ") + v.name + "  " + ((v.ips && v.ips[0]) || (v.nets || []).find((n) => n.startsWith("169.254")) || "") + (v.oper === "up" ? "" : "  (down)")}
                  </text>
                ))}
                {vifPeers.length ? <text x={PX + 18} y={vifCard.y + 64 + VIF.length * ROW} fontSize="11.5" fontFamily={MONO} fill="#475569">{"SA peers: " + vifPeers.join(", ")}</text> : null}
              </g>
            );
          })()}
        </svg>
      </div>
    </div>
  );
}
